//frontend/src/components/Dossier/CreateDossierWithType.jsx

import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { FaBriefcase, FaGraduationCap, FaPlane, FaUsers, FaBuilding, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';

const CreateDossierWithType = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [profileComplete, setProfileComplete] = useState(false);
    const [hasActiveDossier, setHasActiveDossier] = useState(false);
    const [selectedVisaType, setSelectedVisaType] = useState('');

    useEffect(() => {
        checkPrerequisites();
    }, []);

    const checkPrerequisites = async () => {
        try {
            const userResponse = await api.get('/user');
            const profileCompletion = userResponse.data.user.profile_completion_percentage || 0;
            setProfileComplete(profileCompletion >= 80);

            const dossiersResponse = await api.get('/dossiers/');
            const dossiers = Array.isArray(dossiersResponse.data) ? dossiersResponse.data : [];
            const activeDossiers = dossiers.filter(d => 
                d.status !== 'approved' && d.status !== 'rejected'
            );
            setHasActiveDossier(activeDossiers.length > 0);
        } catch (err) {
            console.error('Error checking prerequisites:', err);
        }
    };

    const visaTypes = [
        {
            value: 'work',
            label: 'Visa de Travail',
            icon: <FaBriefcase size={40} />,
            description: 'Pour travailler au Canada de manière temporaire ou permanente',
            color: '#3498db'
        },
        {
            value: 'study',
            label: 'Visa d\'Études',
            icon: <FaGraduationCap size={40} />,
            description: 'Pour poursuivre des études dans un établissement canadien',
            color: '#9b59b6'
        },
        {
            value: 'tourist',
            label: 'Visa Touristique',
            icon: <FaPlane size={40} />,
            description: 'Pour visiter le Canada en tant que touriste',
            color: '#e74c3c'
        },
        {
            value: 'family',
            label: 'Regroupement Familial',
            icon: <FaUsers size={40} />,
            description: 'Pour rejoindre un membre de votre famille au Canada',
            color: '#27ae60'
        },
        {
            value: 'business',
            label: 'Visa d\'Affaires',
            icon: <FaBuilding size={40} />,
            description: 'Pour des activités commerciales ou professionnelles',
            color: '#f39c12'
        }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedVisaType) {
            toast.error('Veuillez sélectionner un type de visa');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const result = await api.post('/dossiers/', {
                visa_type: selectedVisaType
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            toast.success('✅ Dossier créé avec succès !');
            setTimeout(() => navigate(`/dossier/${result.data.dossier.id}`), 800);
        } catch (err) {
            console.error('Error creating dossier:', err);
            const errorMessage = err.response?.data?.message || 
                                err.response?.data?.error || 
                                'Erreur lors de la création du dossier';
            setError(errorMessage);
            toast.error('❌ ' + errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!profileComplete) {
        return (
            <Container className="py-5">
                <Card className="text-center shadow-lg" style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <Card.Body className="p-5">
                        <FaExclamationTriangle size={60} className="text-warning mb-4" />
                        <h3>Profil incomplet</h3>
                        <p className="text-muted mb-4">
                            Vous devez compléter au moins 80% de votre profil avant de créer un dossier de demande de visa.
                        </p>
                        <Button 
                            variant="primary" 
                            size="lg"
                            onClick={() => navigate('/profile/complete')}
                        >
                            Compléter mon profil
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (hasActiveDossier) {
        return (
            <Container className="py-5">
                <Card className="text-center shadow-lg" style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <Card.Body className="p-5">
                        <FaExclamationTriangle size={60} className="text-info mb-4" />
                        <h3>Dossier en cours</h3>
                        <p className="text-muted mb-4">
                            Vous avez déjà un dossier en cours de traitement. Vous ne pouvez créer un nouveau dossier qu'une fois le précédent traité.
                        </p>
                        <Button 
                            variant="primary" 
                            size="lg"
                            onClick={() => navigate('/mes-dossiers')}
                        >
                            Voir mes dossiers
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <h2 className="text-center mb-3">Créer une nouvelle demande</h2>
                    <p className="text-center text-muted">
                        Sélectionnez le type de visa pour lequel vous souhaitez faire une demande
                    </p>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
                <Row className="g-4 mb-4">
                    {visaTypes.map((visa) => (
                        <Col md={6} lg={4} key={visa.value}>
                            <Card 
                                className={`visa-type-card h-100 ${selectedVisaType === visa.value ? 'selected' : ''}`}
                                onClick={() => setSelectedVisaType(visa.value)}
                                style={{ 
                                    cursor: 'pointer',
                                    borderWidth: selectedVisaType === visa.value ? '3px' : '1px',
                                    borderColor: selectedVisaType === visa.value ? visa.color : '#dee2e6',
                                    transition: 'all 0.3s',
                                    transform: selectedVisaType === visa.value ? 'scale(1.05)' : 'scale(1)'
                                }}
                            >
                                <Card.Body className="text-center p-4">
                                    <div style={{ color: visa.color }} className="mb-3">
                                        {visa.icon}
                                    </div>
                                    <h5 className="mb-3">{visa.label}</h5>
                                    <p className="text-muted small mb-0">
                                        {visa.description}
                                    </p>
                                    {selectedVisaType === visa.value && (
                                        <div className="mt-3">
                                            <i className="fas fa-check-circle" style={{ color: visa.color, fontSize: '24px' }}></i>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {selectedVisaType && (
                    <Alert variant="info" className="mb-4">
                        <i className="fas fa-info-circle me-2"></i>
                        Après avoir créé votre dossier, vous pourrez téléverser les documents nécessaires.
                    </Alert>
                )}

                <div className="text-center">
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={loading || !selectedVisaType}
                        className="px-5"
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Création en cours...
                            </>
                        ) : (
                            'Créer mon dossier'
                        )}
                    </Button>
                    <br />
                    <Button
                        variant="link"
                        className="mt-3"
                        onClick={() => navigate('/dashboard')}
                    >
                        Annuler
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CreateDossierWithType;